// Worldwide IANA time zones for the site form.
//
// Sourced from Intl.supportedValuesOf('timeZone') — the browser's own tz
// database (~418 zones), so there's no list to bundle or keep up to date. It's
// supported in every browser this app targets; the fallback below covers the
// rare engine that lacks it, and the field still accepts whatever the server
// already stored either way.

/** A zone plus its current UTC offset, e.g. "Asia/Jerusalem (UTC+03:00)". */
export interface TimezoneOption {
  /** IANA identifier — what gets persisted. */
  value: string;
  /** Display label: identifier + current offset. */
  label: string;
  /** Minutes east of UTC right now, for sorting. */
  offsetMinutes: number;
}

// Minimal fallback if Intl.supportedValuesOf is unavailable — one representative
// zone per offset, so the field stays usable rather than empty.
const FALLBACK_ZONES = [
  'Pacific/Midway', 'Pacific/Honolulu', 'America/Anchorage', 'America/Los_Angeles',
  'America/Denver', 'America/Chicago', 'America/New_York', 'America/Sao_Paulo',
  'Atlantic/Azores', 'UTC', 'Europe/London', 'Europe/Paris', 'Europe/Athens',
  'Asia/Jerusalem', 'Asia/Dubai', 'Asia/Karachi', 'Asia/Kolkata', 'Asia/Dhaka',
  'Asia/Bangkok', 'Asia/Shanghai', 'Asia/Tokyo', 'Australia/Sydney', 'Pacific/Auckland',
];

/** Current UTC offset for a zone, in minutes east of UTC (0 if undeterminable). */
function offsetMinutesFor(timeZone: string): number {
  try {
    // Format a fixed instant in the zone and in UTC, then diff the two.
    const now = new Date();
    const dtf = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hour12: false,
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
    const parts = dtf.formatToParts(now).reduce<Record<string, string>>((acc, p) => {
      if (p.type !== 'literal') acc[p.type] = p.value;
      return acc;
    }, {});
    // 'hour' can come back as '24' for midnight in some engines.
    const hour = parts.hour === '24' ? '00' : parts.hour;
    const asUtc = Date.UTC(
      Number(parts.year), Number(parts.month) - 1, Number(parts.day),
      Number(hour), Number(parts.minute), Number(parts.second),
    );
    return Math.round((asUtc - Math.floor(now.getTime() / 1000) * 1000) / 60000);
  } catch {
    return 0;
  }
}

function formatOffset(minutes: number): string {
  const sign = minutes < 0 ? '-' : '+';
  const abs = Math.abs(minutes);
  const hh = String(Math.floor(abs / 60)).padStart(2, '0');
  const mm = String(abs % 60).padStart(2, '0');
  return `UTC${sign}${hh}:${mm}`;
}

let cached: TimezoneOption[] | null = null;

/**
 * Every IANA zone the browser knows, sorted west → east then alphabetically,
 * so the list reads like a trip around the globe rather than an alphabet soup.
 * Computed once per session — the offsets only shift on DST boundaries.
 */
export function timezoneOptions(): TimezoneOption[] {
  if (cached) return cached;
  const supported = (Intl as unknown as { supportedValuesOf?: (k: string) => string[] })
    .supportedValuesOf;
  const zones = typeof supported === 'function'
    ? supported('timeZone')
    : FALLBACK_ZONES;

  cached = zones
    .map((value) => {
      const offsetMinutes = offsetMinutesFor(value);
      return { value, offsetMinutes, label: `${value} (${formatOffset(offsetMinutes)})` };
    })
    .sort((a, b) => a.offsetMinutes - b.offsetMinutes || a.value.localeCompare(b.value));
  return cached;
}

/** The viewer's own zone, used to preselect a sensible default. */
export function localTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}
